import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, FileDown, Sheet, Send, Loader2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import { downloadFile } from "@/lib/training";
import { PageHeader } from "@/components/Badges";

const ALL = "all";
const pct = (v) => (v == null ? "—" : `${v}%`);

const Tile = ({ label, value, sub, definition, testId }) => (
  <div className="rounded-2xl border border-lasso-line bg-white p-5" data-testid={testId}>
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
      {label}
      {definition && (
        <Tooltip><TooltipTrigger asChild><button data-testid={`${testId}-info`}><Info className="h-3.5 w-3.5 text-zinc-400" /></button></TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">{definition}</TooltipContent></Tooltip>
      )}
    </p>
    <p className="mt-2 font-display text-3xl font-bold" data-testid={`${testId}-value`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
  </div>
);

const Chart = ({ data, k, color, label, suffix = "", testId }) => (
  <div className="rounded-2xl border border-lasso-line bg-white p-5" data-testid={testId}>
    <p className="mb-3 text-sm font-semibold">{label}</p>
    <div className="h-44">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ left: -18, right: 8, top: 4 }}>
          <CartesianGrid stroke="#eef0f3" vertical={false} />
          <XAxis dataKey="week" tickFormatter={(w) => w.slice(5)} tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <RTooltip formatter={(v) => `${v}${suffix}`} />
          <Line type="monotone" dataKey={k} stroke={color} strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const Change = ({ v }) => v == null ? <span className="text-zinc-400">—</span> : (
  <span className={`inline-flex items-center gap-0.5 font-semibold ${v > 0 ? "text-emerald-700" : v < 0 ? "text-rose-600" : "text-zinc-500"}`}>
    {v > 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : v < 0 ? <ArrowDownRight className="h-3.5 w-3.5" /> : null}{v > 0 ? "+" : ""}{v} pts
  </span>
);

const RankTable = ({ title, rows, onPick, testId }) => (
  <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white" data-testid={testId}>
    <p className="border-b border-lasso-line px-5 py-3 text-sm font-semibold">{title}</p>
    <table className="w-full text-sm">
      <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500"><tr><th className="px-5 py-2">#</th><th>Name</th><th>Score</th><th>vs last week</th><th>Overdue</th><th className="pr-5">Forms</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id} className={`border-t border-lasso-line ${onPick ? "cursor-pointer hover:bg-lasso-tint" : ""}`} onClick={() => onPick?.(r)} data-testid={`${testId}-row-${r.id}`}>
            <td className="px-5 py-3 font-mono text-xs">{i + 1}</td>
            <td className="font-medium">{r.name}{r.region && <span className="block text-xs text-zinc-400">{r.region}</span>}</td>
            <td className="font-semibold">{pct(r.score)}</td><td><Change v={r.change} /></td><td>{r.overdue}</td><td className="pr-5" data-testid={`${testId}-forms-${r.id}`}>{pct(r.forms_pct)}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={6} className="px-5 py-6 text-center text-zinc-400">No data in this scope.</td></tr>}
      </tbody>
    </table>
  </div>
);

function Drill({ community, filters, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!community) return;
    setD(null);
    api.get(`/compliance/communities/${community.id}`, { params: filters }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [community, filters]);
  return (
    <Dialog open={!!community} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="compliance-drill-dialog">
        <DialogHeader><DialogTitle className="font-display">{community?.name} · score {pct(d?.metrics.score)}</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : d.people.map((p) => (
          <div key={p.user_id} className="rounded-xl border border-lasso-line p-4" data-testid={`drill-person-${p.user_id}`}>
            <p className="font-semibold">{p.name} <span className="text-xs font-normal text-zinc-500">{ROLE_LABEL[p.role]} · {p.items.length} of {p.total} items need attention</span></p>
            {p.items.length ? (
              <ul className="mt-2 space-y-1 text-sm">{p.items.map((it, i) => (
                <li key={i} className="flex justify-between gap-3"><span>{it.item_type === "course" ? "Course" : "SOP"} · {it.title}</span>
                  <span className={`shrink-0 text-xs font-semibold ${it.status === "late" || it.status === "cert_expiring" ? "text-amber-700" : "text-rose-600"}`}>{it.status_label}{it.days_late ? ` · ${it.days_late}d late` : ""}</span></li>
              ))}</ul>
            ) : <p className="mt-1 text-sm text-emerald-700">All compliant.</p>}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function ReportAdmin() {
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/admin/compliance-settings").then((r) => { setS(r.data); setText(r.data.extra_recipients.join(", ")); }); }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/compliance-settings", { extra_recipients: text.split(/[,\s]+/).filter(Boolean), include_admins: s.include_admins, include_forms_in_score: s.include_forms_in_score });
      setS(data);
      toast.success("Report recipients saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const send = async (test) => {
    if (!window.confirm(`Email the ${test ? "[TEST] " : ""}monthly report now to: ${s.resolved_recipients.join(", ") || "nobody"}?`)) return;
    setBusy(test ? "test" : "send");
    try {
      const { data } = await api.post("/admin/compliance/send-report", { test });
      toast.success(data.results.map((r) => `${r.to}: ${r.status}`).join(" · ") || "No recipients");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  if (!s) return null;
  return (
    <section className="mt-12 rounded-2xl border border-lasso-line bg-white p-6" data-testid="compliance-report-admin">
      <p className="font-display text-sm font-bold uppercase tracking-[0.14em]">Monthly report email</p>
      <p className="mt-1 text-sm text-muted-foreground">Sent automatically on the 1st of each month with the org-wide PDF attached. Every send appears in the email log.</p>
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="extra recipients, comma-separated" className="md:max-w-md" data-testid="compliance-recipients-input" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={s.include_admins} onCheckedChange={(v) => setS({ ...s, include_admins: v })} data-testid="compliance-include-admins" /> Include active admins (non-demo)</label>
        <button onClick={save} className="rounded-full border border-lasso-line px-4 py-2 text-sm font-semibold hover:border-lasso-ink" data-testid="compliance-recipients-save">Save</button>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm"><Switch checked={!!s.include_forms_in_score} onCheckedChange={(v) => setS({ ...s, include_forms_in_score: v })} data-testid="compliance-include-forms-switch" /> Include form completion in overall score (save to apply)</label>
      <p className="mt-2 text-xs text-zinc-500" data-testid="compliance-resolved-recipients">Will send to: {s.resolved_recipients.join(", ") || "nobody"}</p>
      <div className="mt-4 flex gap-2">
        <button onClick={() => send(false)} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-4 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="compliance-send-report-button">{busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send monthly report now</button>
        <button onClick={() => send(true)} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full border border-lasso-line px-4 py-2.5 text-sm font-semibold hover:border-lasso-ink" data-testid="compliance-send-test-button">{busy === "test" && <Loader2 className="h-4 w-4 animate-spin" />} Send [TEST] report</button>
      </div>
    </section>
  );
}

const FormsSection = ({ m, trend, definition, missing }) => (
  <section className="mt-12" data-testid="compliance-forms-section">
    <p className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Required forms completion</p>
    <div className="grid gap-4 lg:grid-cols-3">
      <Tile label="Required forms completion" value={pct(m.pct)} definition={definition} testId="compliance-tile-forms" sub={`${m.complete} of ${m.total} logged events complete · ${m.missing} missing forms`} />
      <div className="lg:col-span-2"><Chart data={trend} k="forms_pct" color="#2F855A" label="Required forms completion (12 weeks)" suffix="%" testId="compliance-trend-forms" /></div>
    </div>
    <div className="mt-4 overflow-hidden rounded-2xl border border-lasso-line bg-white" data-testid="compliance-missing-forms">
      <p className="border-b border-lasso-line px-5 py-3 text-sm font-semibold">Missing forms ({missing.length})</p>
      {missing.map((r) => (
        <Link key={r.id} to={`/form-records/${r.id}`} className="flex flex-col gap-1 border-t border-lasso-line px-5 py-3 text-sm first:border-t-0 hover:bg-lasso-tint md:flex-row md:items-center md:justify-between" data-testid={`compliance-missing-${r.id}`}>
          <span><span className="font-mono text-xs text-zinc-400">{r.event_date}</span> · <span className="font-medium">{r.event_type}</span> · {r.resident}, lot {r.lot} · {r.community}</span>
          <span className="text-xs font-semibold text-amber-800">{r.missing.join(", ")}</span>
        </Link>
      ))}
      {!missing.length && <p className="px-5 py-6 text-center text-sm text-emerald-700">Every logged event has its required forms on file.</p>}
    </div>
  </section>
);

const FilterSelect = ({ value, onChange, label, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 rounded-full bg-white md:w-56" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value={ALL}>{label}</SelectItem>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Compliance() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ region_id: ALL, community_id: ALL, role: ALL, item: ALL });
  const [ov, setOv] = useState(null);
  const [drill, setDrill] = useState(null);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ALL));
  const key = JSON.stringify(params);

  useEffect(() => { api.get("/compliance/filters").then((r) => setOpts(r.data)); }, []);
  const load = useCallback(() => { setOv(null); api.get("/compliance/overview", { params: JSON.parse(key) }).then((r) => setOv(r.data)).catch((e) => toast.error(formatApiError(e))); }, [key]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v, ...(k === "region_id" ? { community_id: ALL } : {}) }));
  const communities = (opts?.communities || []).filter((c) => f.region_id === ALL || c.region_id === f.region_id);
  const m = ov?.metrics;
  const exportQs = new URLSearchParams(params).toString();
  return (
    <TooltipProvider>
      <div className="fade-up">
        <PageHeader eyebrow="Compliance" title={`Compliance · ${ov?.scope_label || ""}`} description="Live numbers calculated from assignments, completions, acknowledgments and certificates."
          actions={
            <div className="flex gap-2">
              <button onClick={() => downloadFile(`/compliance/export.pdf${f.region_id !== ALL ? `?region_id=${f.region_id}` : ""}`, "Lasso-Hub-Compliance.pdf")} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-4 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="compliance-export-pdf"><FileDown className="h-4 w-4" /> PDF report</button>
              <button onClick={() => downloadFile(`/compliance/export.xlsx${exportQs ? `?${exportQs}` : ""}`, "Lasso-Hub-Compliance.xlsx")} className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-4 py-2.5 text-sm font-semibold hover:border-lasso-ink" data-testid="compliance-export-xlsx"><Sheet className="h-4 w-4" /> Spreadsheet</button>
            </div>
          } />
        {opts && (
          <div className="mb-8 flex flex-col flex-wrap gap-3 md:flex-row" data-testid="compliance-filters">
            {isAdmin && <FilterSelect value={f.region_id} onChange={(v) => set("region_id", v)} label="All regions" options={opts.regions} testId="compliance-filter-region" />}
            <FilterSelect value={f.community_id} onChange={(v) => set("community_id", v)} label="All communities" options={communities} testId="compliance-filter-community" />
            <FilterSelect value={f.role} onChange={(v) => set("role", v)} label="All roles" options={opts.roles} testId="compliance-filter-role" />
            <FilterSelect value={f.item} onChange={(v) => set("item", v)} label="All courses & SOPs" options={opts.items} testId="compliance-filter-item" />
          </div>
        )}
        {!m ? <Loader2 className="h-6 w-6 animate-spin text-zinc-400" /> : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Tile label="Overall score" value={pct(m.score)} definition={ov.definitions.score} testId="compliance-tile-score" sub={m.forms_in_score ? "Average of five components (incl. forms)" : "Average of the four components"} />
              <Tile label="Required courses" value={pct(m.course.pct)} definition={ov.definitions.course} testId="compliance-tile-course"
                sub={`${m.course.on_time} on time · ${m.course.late} late · ${m.course.overdue} overdue · ${m.course.not_yet_due} not yet due`} />
              <Tile label="SOP acknowledged" value={pct(m.sop_ack.pct)} definition={ov.definitions.sop_ack} testId="compliance-tile-sop-ack" sub={`${m.sop_ack.acknowledged} of ${m.sop_ack.total} assigned`} />
              <Tile label="SOP latest version" value={pct(m.sop_version.pct)} definition={ov.definitions.sop_version} testId="compliance-tile-sop-version" sub={`${m.sop_version.latest} of ${m.sop_version.acknowledged} acknowledgments`} />
              <Tile label="Certificates current" value={pct(m.certs.pct)} definition={ov.definitions.certs} testId="compliance-tile-certs" sub={`${m.certs.current} current · ${m.certs.expiring} expiring ≤30d · ${m.certs.expired} expired`} />
              <Tile label="Overdue items" value={m.overdue} testId="compliance-tile-overdue" sub="Courses (incl. retraining) + SOPs" />
              <Tile label="Avg days late" value={m.avg_days_late} definition={ov.definitions.days_late} testId="compliance-tile-days-late" />
            </div>
            <div className="mt-8 grid gap-4 lg:grid-cols-3" data-testid="compliance-trends">
              <Chart data={ov.trend} k="score" color="#2E5E8C" label="Compliance score (12 weeks)" suffix="%" testId="compliance-trend-score" />
              <Chart data={ov.trend} k="overdue" color="#C0392B" label="Overdue items" testId="compliance-trend-overdue" />
              <Chart data={ov.trend} k="avg_days_late" color="#B7791F" label="Average days late" testId="compliance-trend-days-late" />
            </div>
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {ov.rankings.regions && <RankTable title="Regions" rows={ov.rankings.regions} testId="compliance-rank-regions" onPick={(r) => set("region_id", r.id)} />}
              <RankTable title="Communities — click to see people" rows={ov.rankings.communities} testId="compliance-rank-communities" onPick={setDrill} />
            </div>
            <FormsSection m={m.forms} trend={ov.trend} definition={ov.definitions.forms} missing={ov.missing_forms} />
          </>
        )}
        {user.is_super && <ReportAdmin />}
        <Drill community={drill} filters={Object.fromEntries(Object.entries(params).filter(([k]) => k === "role" || k === "item"))} onClose={() => setDrill(null)} />
      </div>
    </TooltipProvider>
  );
}
